import React, { useCallback } from "react"
import { Controller, ControllerProps, useFormContext } from "react-hook-form"
import dynamic from "next/dynamic"
import { PhoneInputProps } from "@silversea-ssc/ui-components-react"
import { i18n } from "@lingui/core"
import { t } from "@lingui/macro"
import type { Country as PhoneNumberCountry } from "react-phone-number-input"
import InputWithError from "./InputWithError"

const PhoneInput = dynamic<PhoneInputProps>(
    () => import("@silversea-ssc/ui-components-react").then((mod) => mod.PhoneInput),
    { ssr: false },
)

interface PhoneInputControlProps {
    name: string
    defaultValue?: unknown
    label: string
    disabled?: boolean
    countryCode: string | undefined
}

const PhoneInputControl: React.FC<React.PropsWithChildren<PhoneInputControlProps>> = ({
    name,
    label,
    defaultValue = "",
    disabled = false,
    countryCode,
}) => {
    const formMethods = useFormContext()
    const { control } = formMethods

    const render: ControllerProps["render"] = useCallback(
        ({ field }) => {
            // NOTE: passing ref to PhoneInput breaks the component
            // which does not show the error message anymore
            const { ref, ...rest } = field
            return (
                <InputWithError name={name} label={label} forceLabelRenderAsFocused>
                    {/*
                        FIXME: PhoneInput.country is of type `PhoneNumberCountry | undefined`, but here it says it's `any`.
                        We are passing countryCode that is `string | undefined` but since it believes it's `any` tsc doesn't emit any error.
                        Cypress with ts-loader instead catches this error.
                        Is there a better solution than this?
                    */}
                    <PhoneInput country={countryCode as PhoneNumberCountry} {...rest} disabled={disabled} />
                </InputWithError>
            )
        },
        [countryCode, disabled, label, name],
    )
    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={{
                required: i18n._(t`form-required-phone-number`),
                validate: async (value: string) => {
                    const { isValidPhoneNumber } = await import("react-phone-number-input/min")

                    if (value && !isValidPhoneNumber(value)) return i18n._(t`form-invalid-phone-number`)

                    return true
                },
            }}
            render={render}
        />
    )
}

export default PhoneInputControl
